/**
 * useVideoAssistant Hook
 *
 * Manages the state and API communication for the video assistant chat panel.
 * Handles message history, sending messages to the chat API, and parsing
 * VIDEO_CONFIG blocks from AI responses.
 *
 * Supports SSE streaming for real-time token display.
 */

import { useState, useCallback, useRef } from 'react';
import { URLService } from '@/services';
import { extractResponseErrorMessage, getAuthHeadersFromStorage } from '@/utils/httpUtils';
import { extractVideoConfig, removeVideoConfigBlock } from '@/utils/videoConfigParser';
import {
  VIDEO_ASSISTANT_SYSTEM_PROMPT,
  VIDEO_ASSISTANT_MAX_HISTORY,
  VIDEO_ASSISTANT_TEMPERATURE,
  VIDEO_ASSISTANT_MAX_TOKENS,
} from '@/constants/videoAssistant';
import type { VideoConfig } from '@/types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  /** Parsed config extracted from this message (assistant only) */
  parsedConfig?: Partial<VideoConfig> | null;
}

interface ChatCompletionRequest {
  messages: Array<{ role: string; content: string }>;
  model: string;
  provider: string;
  temperature: number;
  max_tokens: number;
  stream: boolean;
}

interface UseVideoAssistantOptions {
  llmProvider: string;
  llmModel: string;
  onConfigUpdate: (config: Partial<VideoConfig>) => void;
}

/**
 * Parse SSE lines from a text chunk. Handles partial lines that may span
 * across multiple ReadableStream reads.
 */
function parseSSELines(text: string): Array<Record<string, unknown>> {
  const events: Array<Record<string, unknown>> = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data: ')) continue;
    try {
      events.push(JSON.parse(trimmed.slice(6)) as Record<string, unknown>);
    } catch {
      // Partial JSON from chunked transfer - skip
    }
  }
  return events;
}

export function useVideoAssistant({
  llmProvider,
  llmModel,
  onConfigUpdate,
}: UseVideoAssistantOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      setError(null);

      // Add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        // Cancel any in-flight request
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const apiUrl = URLService.getAPIBaseUrl();
        const headers = await getAuthHeadersFromStorage();

        // Build API messages: system prompt + conversation history (limited)
        const apiMessages: Array<{ role: string; content: string }> = [
          { role: 'system', content: VIDEO_ASSISTANT_SYSTEM_PROMPT },
        ];

        // Add recent conversation history (skip empty content to avoid 422)
        const recentMessages = messages.slice(-VIDEO_ASSISTANT_MAX_HISTORY);
        for (const msg of recentMessages) {
          if (msg.content.trim()) {
            apiMessages.push({ role: msg.role, content: msg.content });
          }
        }

        // Add current user message
        apiMessages.push({ role: 'user', content: trimmed });

        const request: ChatCompletionRequest = {
          messages: apiMessages,
          model: llmModel,
          provider: llmProvider,
          temperature: VIDEO_ASSISTANT_TEMPERATURE,
          max_tokens: VIDEO_ASSISTANT_MAX_TOKENS,
          stream: true,
        };

        const response = await fetch(`${apiUrl}/api/v1/chat/completion`, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            extractResponseErrorMessage(errorData) || `Request failed (${response.status})`
          );
        }

        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('text/event-stream') && response.body) {
          // =================================================================
          // STREAMING PATH: Read SSE chunks progressively
          // =================================================================
          const assistantId = `assistant-${Date.now()}`;
          setMessages((prev) => [
            ...prev,
            { id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
          ]);
          setIsLoading(false);
          setIsStreaming(true);

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let fullResponse = '';
          let streamError = false;

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const text = decoder.decode(value, { stream: true });
              const events = parseSSELines(text);

              for (const data of events) {
                if (data.error) {
                  setError(data.error as string);
                  streamError = true;
                  break;
                }

                if (data.done) {
                  break;
                }

                if (data.content) {
                  fullResponse += data.content as string;
                  // Update message content progressively
                  const currentContent = fullResponse;
                  setMessages((prev) => {
                    const last = prev[prev.length - 1];
                    if (last?.id === assistantId) {
                      return [...prev.slice(0, -1), { ...last, content: currentContent }];
                    }
                    return prev;
                  });
                }
              }

              if (streamError) break;
            }
          } finally {
            setIsStreaming(false);
          }

          // After stream: parse VIDEO_CONFIG from full response
          if (!streamError && fullResponse) {
            const parsedConfig = extractVideoConfig(fullResponse);
            const stripped = parsedConfig ? removeVideoConfigBlock(fullResponse) : fullResponse;
            const displayContent = stripped || fullResponse;

            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.id === assistantId) {
                return [...prev.slice(0, -1), { ...last, content: displayContent, parsedConfig }];
              }
              return prev;
            });

            if (parsedConfig) {
              onConfigUpdate(parsedConfig);
            }
          }
        } else {
          // =================================================================
          // NON-STREAMING FALLBACK: Parse full JSON response
          // =================================================================
          const data = await response.json();
          const content = data.message?.content || '';

          // Parse config from response
          const parsedConfig = extractVideoConfig(content);
          const stripped = parsedConfig ? removeVideoConfigBlock(content) : content;
          const displayContent = stripped || content;

          const assistantMessage: ChatMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: displayContent,
            timestamp: new Date(),
            parsedConfig,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          if (parsedConfig) {
            onConfigUpdate(parsedConfig);
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const message = err instanceof Error ? err.message : 'Failed to send message';
        setError(message);
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
      }
    },
    [messages, isLoading, llmProvider, llmModel, onConfigUpdate]
  );

  const clearMessages = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    clearMessages,
  };
}
